import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface Product {
  id: string;
  name: string;
  brand: string;
  rating: number;
  reviews_count: number;
  description: string;
  price: number;
  original_price: number | null;
  discount: number;
  image: string;
  badge: string;
  category: string;
  in_stock: boolean;
}

interface ProductFormData {
  name: string;
  brand: string;
  category: string;
  price: string;
  original_price: string;
  discount: string;
  image: string;
  badge: string;
  description: string;
  in_stock: boolean;
}

const emptyForm: ProductFormData = {
  name: '', brand: '', category: '', price: '', original_price: '', discount: '0',
  image: '', badge: '', description: '', in_stock: true,
};

export default function ProductsManager() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProductFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase.from('products').select('*').order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setProducts(data || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (p: Product) => {
    setEditingId(p.id);
    setForm({
      name: p.name, brand: p.brand, category: p.category,
      price: String(p.price), original_price: p.original_price ? String(p.original_price) : '',
      discount: String(p.discount), image: p.image, badge: p.badge,
      description: p.description, in_stock: p.in_stock,
    });
    setFormError('');
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this product? This cannot be undone.')) return;
    try {
      const { error: delError } = await supabase.from('products').delete().eq('id', id);
      if (delError) throw delError;
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!form.name || !form.brand || !form.price) {
      setFormError('Name, brand, and price are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        brand: form.brand,
        category: form.category,
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        discount: parseInt(form.discount) || 0,
        image: form.image,
        badge: form.badge,
        description: form.description,
        in_stock: form.in_stock,
      };

      if (editingId) {
        const { error: updError } = await supabase.from('products').update(payload).eq('id', editingId);
        if (updError) throw updError;
      } else {
        const { error: insError } = await supabase.from('products').insert({ ...payload, rating: 0, reviews_count: 0 });
        if (insError) throw insError;
      }
      setShowForm(false);
      fetchProducts();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-20 text-center text-ink-500">Loading products...</div>;
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="text-rose-600 text-sm mb-3">{error}</p>
        <button onClick={fetchProducts} className="h-9 px-4 rounded-lg bg-ink-900 text-white text-sm font-semibold whitespace-nowrap">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-bold text-ink-900">
          Products ({products.length})
        </h2>
        <button onClick={openAdd} className="h-9 px-4 rounded-lg bg-ink-900 hover:bg-ink-800 text-white text-sm font-semibold whitespace-nowrap flex items-center gap-1.5 transition">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line"></i>
          </span>
          Add Product
        </button>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl border border-ink-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-ink-50/70 border-b border-ink-100">
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Product</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Brand</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Category</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Price</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Rating</th>
                <th className="text-right text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} className="border-b border-ink-100 last:border-b-0 hover:bg-ink-50/50 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.image && (
                        <div className="w-10 h-10 rounded-lg bg-ink-100 overflow-hidden flex-shrink-0">
                          <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-ink-900 truncate max-w-[280px]">{p.name}</p>
                        {p.badge && <span className="text-[10px] font-semibold uppercase text-ink-400">{p.badge}</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-ink-700">{p.brand}</td>
                  <td className="px-4 py-3 text-sm text-ink-700">{p.category}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-ink-900">
                    ${p.price}
                    {p.original_price && p.original_price > p.price && (
                      <span className="ml-1.5 text-xs text-ink-400 line-through">${p.original_price}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-star-fill text-amber-500 text-xs"></i>
                      </span>
                      <span className="text-sm font-semibold text-ink-800">{p.rating}</span>
                      <span className="text-xs text-ink-400">({p.reviews_count})</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(p)} className="w-8 h-8 rounded-lg hover:bg-ink-100 text-ink-600 flex items-center justify-center transition" title="Edit">
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button onClick={() => handleDelete(p.id)} className="w-8 h-8 rounded-lg hover:bg-rose-50 text-ink-600 hover:text-rose-600 flex items-center justify-center transition" title="Delete">
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
          <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm" onClick={() => setShowForm(false)}></div>
          <div className="relative bg-white rounded-2xl border border-ink-100 shadow-card w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-ink-100 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-ink-900">
                {editingId ? 'Edit Product' : 'Add Product'}
              </h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-lg hover:bg-ink-50 flex items-center justify-center text-ink-500 transition">
                <i className="ri-close-line"></i>
              </button>
            </div>

            {formError && (
              <div className="mx-6 mt-4 p-3 rounded-lg bg-rose-50 text-rose-700 text-sm">{formError}</div>
            )}

            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Name *</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Brand *</label>
                  <input value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Category</label>
                  <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Badge</label>
                  <input value={form.badge} onChange={e => setForm({ ...form, badge: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" placeholder="e.g. Editor's Choice" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Price *</label>
                  <input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Original Price</label>
                  <input type="number" step="0.01" value={form.original_price} onChange={e => setForm({ ...form, original_price: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Discount %</label>
                  <input type="number" value={form.discount} onChange={e => setForm({ ...form, discount: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Image URL</label>
                <input value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition resize-none" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.in_stock} onChange={e => setForm({ ...form, in_stock: e.target.checked })} className="rounded border-ink-300 text-cta-600 focus:ring-cta-400" />
                <span className="text-sm text-ink-700">In Stock</span>
              </label>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="h-9 px-4 rounded-lg border border-ink-200 text-sm font-semibold text-ink-600 hover:bg-ink-50 whitespace-nowrap transition">Cancel</button>
                <button type="submit" disabled={saving} className="h-9 px-4 rounded-lg bg-ink-900 hover:bg-ink-800 text-white text-sm font-semibold whitespace-nowrap transition disabled:opacity-50">
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}